import React from 'react';
import {
    Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow,
} from '@mui/material';
import { ArrowUpward as ArrowUpwardIcon } from '@mui/icons-material';
import { AcceptanceBadge, fmt, fmtPct, fmtSigned } from './msaFormat';

// ---------------------------------------------------------------------------
// CapabilityImpactResults — renders the "capabilityImpact" engine payload.
//
// results = {
//   sigma2Observed, sigma2Measurement, sigma2TrueProcess,
//   measurementContributionPercent, processContributionPercent,   // 0..100
//   observed:    { stdDev, cp|null, cpk|null },
//   trueProcess: { stdDev, cp|null, cpk|null },
//   cpkImprovement|null,
//   recommendation: string,
// }
//
// Acceptance is derived HERE from measurementContributionPercent:
//   <10 acceptable | <30 marginal | else unacceptable.
// ---------------------------------------------------------------------------

const AMBER = '#ffb74d'; // measurement (gauge error)
const BLUE  = '#90caf9'; // true process

function impactStatus(pct) {
    if (pct === null || pct === undefined || !isFinite(pct)) return undefined;
    if (pct < 10) return 'acceptable';
    if (pct < 30) return 'marginal';
    return 'unacceptable';
}

const labelSx = { color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' };
const valueSx = { color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: '0.95rem' };

function VarianceRow({ label, value, pct, swatch }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            {swatch && (
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: swatch, flexShrink: 0, alignSelf: 'center' }} />
            )}
            <Typography sx={{ ...labelSx, minWidth: 130 }}>{label}</Typography>
            <Typography sx={valueSx}>{fmt(value, 6)}</Typography>
            {pct !== undefined && (
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                    ({fmtPct(pct)})
                </Typography>
            )}
        </Box>
    );
}

// Stacked contribution bar: measurement (amber) vs true process (blue).
function ContributionBar({ measurementPct, processPct }) {
    // L12: global isFinite(null) === true (null coerces to 0), so a null processPct would
    // render 0 instead of the intended 100−m complement. Guard null/undefined explicitly.
    const finite = (x) => x !== null && x !== undefined && Number.isFinite(x);
    const m = finite(measurementPct) ? Math.max(0, Math.min(100, measurementPct)) : 0;
    const p = finite(processPct) ? Math.max(0, Math.min(100, processPct)) : Math.max(0, 100 - m);

    return (
        <Box sx={{ mt: 0.5 }}>
            <Box
                sx={{
                    display: 'flex',
                    height: 18,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                }}
            >
                <Box sx={{ width: `${m}%`, backgroundColor: AMBER, transition: 'width 0.3s' }} />
                <Box sx={{ width: `${p}%`, backgroundColor: BLUE, transition: 'width 0.3s' }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography sx={{ color: AMBER, fontSize: '0.72rem', fontWeight: 600 }}>
                    Measurement {fmtPct(measurementPct)}
                </Typography>
                <Typography sx={{ color: BLUE, fontSize: '0.72rem', fontWeight: 600 }}>
                    Process {fmtPct(processPct)}
                </Typography>
            </Box>
        </Box>
    );
}

const headCellSx = {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    py: 1,
    px: 1.5,
    whiteSpace: 'nowrap',
};

const bodyCellSx = {
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.85)',
    fontSize: '0.85rem',
    py: 0.75,
    px: 1.5,
};

function CapabilityImpactResults({ results, meta }) {
    if (!results) return null;

    const {
        sigma2Observed,
        sigma2Measurement,
        sigma2TrueProcess,
        measurementContributionPercent,
        processContributionPercent,
        observed = {},
        trueProcess = {},
        cpkImprovement,
        recommendation,
    } = results;

    const status = impactStatus(measurementContributionPercent);
    const improvementPositive = isFinite(cpkImprovement) && cpkImprovement > 0;

    const capRows = [
        { label: 'Std Dev', obs: observed.stdDev, tru: trueProcess.stdDev },
        { label: 'Cp',      obs: observed.cp,     tru: trueProcess.cp },
        { label: 'Cpk',     obs: observed.cpk,    tru: trueProcess.cpk },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Acceptance headline */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <AcceptanceBadge status={status} label="Measurement Impact" />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                    Gauge error accounts for {fmtPct(measurementContributionPercent)} of observed variance.
                </Typography>
            </Box>

            {/* Variance decomposition */}
            <Paper
                sx={{
                    p: { xs: 1.5, md: 2 },
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                }}
            >
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    Variance Decomposition
                </Typography>
                <VarianceRow label="σ²_observed" value={sigma2Observed} />
                <VarianceRow label="σ²_measurement" value={sigma2Measurement} pct={measurementContributionPercent} swatch={AMBER} />
                <VarianceRow label="σ²_true process" value={sigma2TrueProcess} pct={processContributionPercent} swatch={BLUE} />
                <ContributionBar
                    measurementPct={measurementContributionPercent}
                    processPct={processContributionPercent}
                />
            </Paper>

            {/* Observed vs True capability */}
            <Paper
                sx={{
                    p: { xs: 1.5, md: 2 },
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                }}
            >
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    Capability — Observed vs. Perfect Gauge
                </Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headCellSx}>Metric</TableCell>
                                <TableCell sx={headCellSx} align="right">Observed</TableCell>
                                <TableCell sx={headCellSx} align="right">True (perfect gauge)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {capRows.map((r) => (
                                <TableRow key={r.label}>
                                    <TableCell sx={{ ...bodyCellSx, color: 'rgba(255,255,255,0.6)' }}>{r.label}</TableCell>
                                    <TableCell sx={bodyCellSx} align="right">{fmt(r.obs)}</TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: BLUE }} align="right">{fmt(r.tru)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {cpkImprovement !== null && cpkImprovement !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                        {improvementPositive && (
                            <ArrowUpwardIcon sx={{ fontSize: '1rem', color: '#81c784' }} />
                        )}
                        <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                            Cpk improvement with a perfect gauge:{' '}
                            <Box
                                component="span"
                                sx={{ color: improvementPositive ? '#81c784' : 'rgba(255,255,255,0.9)', fontWeight: 600 }}
                            >
                                {fmtSigned(cpkImprovement)}
                            </Box>
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Recommendation */}
            {recommendation && (
                <Paper
                    sx={{
                        p: { xs: 1.5, md: 2 },
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}
                    >
                        Recommendation
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.88rem', lineHeight: 1.6, mt: 0.5 }}>
                        {recommendation}
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}

export default CapabilityImpactResults;
